import * as React from 'react';

import { addModifiers } from '../../../shared/helpers';

import './CardList.scss';

interface CardListProps {
  modifiers?: string[];
  withContainer?: boolean;
  children: React.ReactNode;
}

interface CardListTitleProps {
  as?: 'h2' | 'h3';
  children: React.ReactNode;
}

interface CardListRowProps {
  modifiers?: string[];
  children: React.ReactNode;
}

interface CardListCellProps {
  modifiers?: string[];
  children: React.ReactNode;
}

const Title = ({ as: Tag = 'h3', children }: CardListTitleProps) => <Tag className="card-list__title">{children}</Tag>;

const Row = ({ modifiers, children }: CardListRowProps) => <li className={addModifiers('card-list__item', modifiers)}>{children}</li>;

const Cell = ({ modifiers, children }: CardListCellProps) => (
  <div className={addModifiers('card-list__item-text', modifiers)}>{children}</div>
);

const CardList = ({ modifiers, withContainer = true, children }: CardListProps) => {
  const elements = React.Children.toArray(children).filter((child): child is React.ReactElement => React.isValidElement(child));
  const titles = elements.filter(child => child.type === Title);
  const rows = elements.filter(child => child.type === Row);

  // Only <CardList.Title> and <CardList.Row> are honored as direct children — anything else
  // (e.g. a stray <CardList.Cell> or other wrapper) is intentionally dropped. Warn in dev so a
  // misplaced child surfaces instead of vanishing silently.
  if (process.env.NODE_ENV !== 'production') {
    elements
      .filter(child => child.type !== Title && child.type !== Row)
      .forEach(child => {
        // eslint-disable-next-line no-console
        console.warn('<CardList> only honors <CardList.Title> and <CardList.Row> as direct children; this child will be dropped:', child);
      });
  }

  const body = (
    <>
      {titles}
      <ul className="card-list__content">{rows}</ul>
    </>
  );

  return withContainer ? <div className={addModifiers('card-list', modifiers)}>{body}</div> : body;
};

CardList.Title = Title;
CardList.Row = Row;
CardList.Cell = Cell;

export { CardList };
